import {
	Box,
	BulkAction,
	Button,
	Flex,
	Grid,
	RiCheckLine,
	RiDownload2Line,
	RiFilterOffLine,
	RiFolderZipLine
} from "@itandi/itandi-bb-ui";
import { Dropdown } from "@itandi/itandi-bb-ui/Dropdown";
import { DropdownMenu } from "@itandi/itandi-bb-ui/DropdownMenu";
import type { ReactNode } from "react";
import React, { useState } from "react";
import type { SortEnd, SortEndHandler } from "react-sortable-hoc";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { renderDropDownButton } from "~/components/common/parts/dropdown/DropDownButton";
import { ImagePreviewModal } from "~/components/common/parts/modal/ImagePreviewModal";
import { ReportBulkDeleteModal } from "~/components/common/reports/ReportBulkDeleteModal";
import { ReportImagesAreaBlock } from "~/components/common/reports/ReportImagesAreaBlock";
import { ReportPdfDownloadModal } from "~/components/common/reports/ReportPdfDownloadModal";
import type {
	DealReportBulkUpdateParams,
	DealReportImage,
	ReportImageBulkAction
} from "~/models/DealReportImage";
import {
	filterSelectedOwnImages,
	hasOperableSelectedImages,
	imagesExistInReport
} from "~/models/DealReportImage";
import type { NskResponse } from "../../../models/Api";
import Styles from "./ReportImagesArea.module.scss";

type StableSortableContainerProps = {
	items: DealReportImage[];
	renderImageBlock: (reportImage: DealReportImage, index: number) => ReactNode;
};

// SortableElementとSortableContainerをコンポーネント外で定義することで、再レンダー時の不要なアンマウントを防ぐ
// コンポーネント内で定義すると、毎回新しいコンポーネント型が生成されてしまう
const SortableItem = SortableElement<{ children: React.ReactNode }>(
	(props: { children: React.ReactNode }) => <>{props.children}</>
);
const SortableList = SortableContainer<StableSortableContainerProps>(
	(props: StableSortableContainerProps) => {
		const { items, renderImageBlock } = props;
		return (
			<div className={Styles.ReportImageList}>
				{items.map(renderImageBlock)}
			</div>
		);
	}
);

type Props = {
	companyType: "company" | "vendor";
	handleBulkDelete: () => void;
	handleBulkUpdate: (submitValue: DealReportBulkUpdateParams) => Promise<void>;
	handleDestroy: (reportImage: DealReportImage) => Promise<NskResponse>;
	handleDownloadImage: (reportImage: DealReportImage) => void;
	handleDownloadPdf: (imagesPerPage: number) => void;
	handleDownloadZip: () => void;
	handleResetSelectedIds: () => void;
	handleSelectAllImages: () => void;
	handleSelectedDownloadPdf: (imagesPerPage: number) => void;
	handleSelectedDownloadZip: () => void;
	handleUpdate: (submitValue: DealReportImage) => Promise<NskResponse>;
	handleUpdateSeq: (newOrderImages: DealReportImage[]) => Promise<void>;
	onChangeSelectedImage: (reportImageId: number) => void;
	reportImages: DealReportImage[];
	selectedImageIds: readonly number[];
};

export function ReportImagesArea({
	reportImages,
	handleDownloadImage,
	handleUpdateSeq,
	handleUpdate,
	handleDestroy,
	handleDownloadPdf,
	handleDownloadZip,
	companyType,
	onChangeSelectedImage,
	selectedImageIds,
	handleSelectedDownloadZip,
	handleSelectedDownloadPdf,
	handleBulkUpdate,
	handleBulkDelete,
	handleResetSelectedIds,
	handleSelectAllImages
}: Props) {
	// 並び替えのためのstate
	const [orderImages, setOrderImages] = useState<DealReportImage[]>([]);
	const isVendor = companyType === "vendor";
	const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
	const [pdfModalMode, setPdfModalMode] = useState<"all" | "selected" | null>(
		null
	);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [initialIndex, setInitialIndex] = useState(0);

	const openPreviewAt = (index: number): void => {
		setInitialIndex(index);
		setIsPreviewOpen(true);
	};
	const closePreview = (): void => {
		setIsPreviewOpen(false);
	};

	React.useEffect(() => {
		setOrderImages(reportImages);
	}, [reportImages]);
	let tmpSeqNo = 0;
	const onSortEnd: SortEndHandler = ({ oldIndex, newIndex }: SortEnd) => {
		tmpSeqNo = 0;
		if (oldIndex === newIndex) {
			return;
		}
		const moveImage = orderImages[oldIndex];
		const newOrderItems: DealReportImage[] = [];
		const wkOrderImages = [...orderImages];
		wkOrderImages.splice(oldIndex, 1);
		wkOrderImages.splice(newIndex, 0, moveImage);
		wkOrderImages.forEach((r: DealReportImage, i: number) => {
			newOrderItems.push({ ...r, seq: i + 1 });
		});
		setOrderImages(newOrderItems);
		handleUpdateSeq(newOrderItems);
	};
	const renderImageBlock = (
		reportImage: DealReportImage,
		index: number
	): ReactNode => {
		const displaySeqNo = ((): number | null => {
			if (isVendor) {
				return index + 1;
			}
			if (reportImage.outputPdfImage) {
				tmpSeqNo += 1;
				return tmpSeqNo;
			}
			return null;
		})();

		return (
			<SortableItem index={index} key={reportImage.id}>
				<ReportImagesAreaBlock
					companyType={companyType}
					displaySeqNo={displaySeqNo}
					handleDestroy={handleDestroy}
					handleDownloadImage={handleDownloadImage}
					handleUpdate={handleUpdate}
					handleUpdateSeq={handleUpdateSeq}
					key={reportImage.id}
					onChangeSelectedImage={onChangeSelectedImage}
					onPreviewOpen={(): void => {
						openPreviewAt(index);
					}}
					reportImage={reportImage}
					selectedImageIds={selectedImageIds}
				/>
			</SortableItem>
		);
	};

	const showBulkAction = React.useMemo(
		(): boolean =>
			hasOperableSelectedImages(reportImages, selectedImageIds, companyType),
		[reportImages, selectedImageIds, companyType]
	);
	const canDownloadReport = imagesExistInReport(orderImages, isVendor);
	const selectedImages = orderImages.filter((img) =>
		selectedImageIds.includes(img.id)
	);
	const canDownloadSelectedReport = imagesExistInReport(
		selectedImages,
		isVendor
	);
	const bulkActionList = (): ReportImageBulkAction[] => {
		const actions: ReportImageBulkAction[] = [];
		actions.push({
			children: "画像をダウンロード",
			onClick: handleSelectedDownloadZip
		});
		actions.push({
			children: "報告書をダウンロード",
			disabled: !canDownloadSelectedReport,
			onClick: () => {
				setPdfModalMode("selected");
			}
		});
		if (showBulkAction) {
			actions.push({
				children: "施工推奨にする",
				onClick: () => handleBulkUpdate({ construction: "targeted" })
			});
			actions.push({
				children: "施工推奨から外す",
				onClick: () => handleBulkUpdate({ construction: "unspecified" })
			});
		}
		if (isVendor) {
			if (showBulkAction) {
				actions.push({
					children: "画像を共有する",
					onClick: () => handleBulkUpdate({ shared: true })
				});
				actions.push({
					children: "画像を共有しない",
					onClick: () => handleBulkUpdate({ shared: false })
				});
			}
		} else {
			actions.push({
				children: "画像を報告書に含める",
				onClick: () => handleBulkUpdate({ outputPdfImage: true })
			});
			actions.push({
				children: "画像を報告書に含めない",
				onClick: () => handleBulkUpdate({ outputPdfImage: false })
			});
			actions.push({
				children: "備考を報告書に含める",
				onClick: () => handleBulkUpdate({ outputPdfNote: true })
			});
			actions.push({
				children: "備考を報告書に含めない",
				onClick: () => handleBulkUpdate({ outputPdfNote: false })
			});
		}
		if (showBulkAction) {
			// 画像の削除は一番下に表示する
			actions.push({
				children: "画像を削除",
				danger: true,
				onClick: () => {
					setIsBulkModalOpen(true);
				}
			});
		}
		return actions;
	};
	return (
		<>
			<div className={Styles.ReportImagesArea}>
				<Grid
					alignItems="center"
					autoFlow="column"
					gap={4}
					justifyContent="space-between"
					marginBottom={8}
				>
					<Box>
						<Flex gap={16} margin="8px 2px">
							<Button
								disabled={!canDownloadReport}
								endIcon={<RiDownload2Line />}
								onClick={() => {
									setPdfModalMode("all");
								}}
								variant="secondary"
							>
								報告書ダウンロード
							</Button>
							<Button
								disabled={orderImages.length <= 0}
								endIcon={<RiFolderZipLine />}
								onClick={handleDownloadZip}
								variant="secondary"
							>
								画像一括ダウンロード
							</Button>
						</Flex>
					</Box>
					<Box>
						<Flex gap={16} margin="8px 2px">
							<Button
								disabled={reportImages.length === 0}
								onClick={handleSelectAllImages}
								startIcon={<RiCheckLine />}
								variant="secondary"
							>
								全てを選択
							</Button>
							<Button
								disabled={selectedImageIds.length === 0}
								onClick={handleResetSelectedIds}
								startIcon={<RiFilterOffLine />}
								variant="secondary"
							>
								選択をクリア
							</Button>
						</Flex>
					</Box>
				</Grid>
				<BulkAction
					actionsArea={
						<Dropdown button={renderDropDownButton("まとめて操作")}>
							{bulkActionList().map((action) => (
								<DropdownMenu
									danger={action.danger}
									disabled={action.disabled}
									key={action.children}
									onClick={action.onClick}
								>
									{action.children}
								</DropdownMenu>
							))}
						</Dropdown>
					}
					selectedCount={selectedImageIds.length}
				>
					<SortableList
						axis="xy"
						items={orderImages}
						onSortEnd={onSortEnd}
						onSortStart={(): void => {
							tmpSeqNo = 0;
						}}
						renderImageBlock={renderImageBlock}
						useDragHandle
					/>
				</BulkAction>
				{pdfModalMode ? (
					<ReportPdfDownloadModal
						onClose={() => {
							setPdfModalMode(null);
						}}
						onDownload={
							pdfModalMode === "all"
								? handleDownloadPdf
								: handleSelectedDownloadPdf
						}
					/>
				) : null}
				{isBulkModalOpen ? (
					<ReportBulkDeleteModal
						deleteCount={
							filterSelectedOwnImages(
								reportImages,
								selectedImageIds,
								companyType
							).length
						}
						onClose={() => {
							setIsBulkModalOpen(false);
						}}
						onDelete={handleBulkDelete}
					/>
				) : null}
			</div>
			{isPreviewOpen ? (
				<ImagePreviewModal
					handleClose={closePreview}
					initialIndex={initialIndex}
					reportImages={orderImages}
				/>
			) : null}
		</>
	);
}
